import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import beActivateTagsTrial from 'actions/billing/beActivateTagsTrial.action';
import beGetTagsTrial from 'actions/billing/beGetTagsTrial.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Space from 'components/generic/Space.component';
import Spinner from 'components/generic/Spinner.component';
import DELAY from 'constants/delay.const';
import S from 'constants/string.const';
import { FeStack } from 'errors/fe.error';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonBilling, selectCurrentSalonId, selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import { selectBillingError } from 'selectors/settings.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { locationReload } from 'utils/dom/location.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import salonSubscription from 'utils/salons/salonSubscription.util';

import CN from './TimeOffSplashStartFreeTrial.module.scss';

const CTAG = 'TimeOffSplashStartFreeTrial';
const L = logger(CTAG);

const SPINNER_COLOR = '#821bb8';
const SPINNER_BACKGROUND_COLOR = 'white';

const TimeOffSplashStartFreeTrial = () => {
  const dispatch = useDispatch();
  const salonId = useSelector(selectCurrentSalonId);
  const errorMessage = useSelector(selectBillingError);
  const hasBilling = useSelector(selectCurrentSalonBilling);
  const pricingPlan = useSelector(selectCurrentSalonPricingPlan);

  const { val: isTrialRendered, yay: showTrial, nay: hideTrial } = useTip(false);
  const { val: isLoading, yay: startLoading, nay: stopLoading } = useTip(false);
  const { val: isErrorRendered, tip: toggleError } = useTip(false);
  const { val: isSuccessRendered, yay: showSuccess, nay: hideSuccess } = useTip(false);

  const { t } = useTranslation();

  const handleStartClick = useCallback(async () => {
    try {
      startLoading();
      const trialStateResult = await dispatch(beGetTagsTrial({ salonId }));
      const name = getAnyOrVoid('response.all_features.0', trialStateResult);
      const activateResult = await dispatch(beActivateTagsTrial({ salonId, name }));

      if (activateResult.error) {
        toggleError();
        hideTrial();
      } else {
        delayed(DELAY.flash, () => {
          showSuccess();
          locationReload(new FeStack({ tag: 'TimeOffSplashStartFreeTrial__start-click' }));
        });
      }
    } catch (e) {
      L.warn('beActivateTagsTrial()', e);
    } finally {
      stopLoading();
      hideSuccess();
    }
  }, [dispatch, salonId, startLoading, stopLoading, toggleError, hideTrial, hideSuccess, showSuccess]);

  const isComponentRendered = salonSubscription(hasBilling, pricingPlan);

  if (!isComponentRendered) {
    return null;
  }

  return (
    <div data-bem="TimeOffSplashStartFreeTrial">
      <div className={CN.component}>
        <Button variant="slim" onClick={showTrial}>
          <small className={CN.textSm}>{t('humanResources.splashOverview.freeTrial.startButton')}</small>
        </Button>
      </div>

      {isLoading && <Spinner color={SPINNER_COLOR} message="" background={SPINNER_BACKGROUND_COLOR} />}

      {fromAnyToBool(isErrorRendered) && (
        <SalonModal tag="TimeOffSplashStartFreeTrialError" isOpen={isErrorRendered}>
          <SalonModal.Body>
            <span className={CN.modalTitle}>{t('humanResources.splashOverview.freeTrial.errorModalTitle')}</span>
            <Space>
              <span className={CN.modalText}>{errorMessage}</span>
            </Space>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={toggleError}>
              {t('common.buttons.closeUpp')}
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}

      {fromAnyToBool(isSuccessRendered) && (
        <SalonModal tag="TimeOffSplashStartFreeTrialSuccess" isOpen={isSuccessRendered}>
          <SalonModal.Body>
            <span className={CN.modalTitle}>{t('humanResources.splashOverview.freeTrial.successModalTitle')}</span>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={hideSuccess}>
              {t('common.buttons.closeUpp')}
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}

      {fromAnyToBool(isTrialRendered) && (
        <SalonModal tag="StartFreeTrial" isOpen={isTrialRendered}>
          <SalonModal.Body>
            <span className={CN.modalTitle}>
              {t('humanResources.splashOverview.freeTrial.startModalTitle')} {S.party}
            </span>
            <Space>
              <span className={CN.modalText}>{t('humanResources.splashOverview.freeTrial.startModalDescription')}</span>
            </Space>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="slim" onClick={hideTrial}>
              {t('common.buttons.cancelUpp')}
            </Button>
            <Button variant="primary" casing="dialog" onClick={handleStartClick}>
              {t('humanResources.splashOverview.freeTrial.startButton')}
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

export default TimeOffSplashStartFreeTrial;
